import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as clientActions from '../../actions/client';
import { withRouter } from 'react-router';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import { ClientObject } from '../../types/client.type';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import { axiosCaller } from '../../utils/apiCaller';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';

import ContactDashboardStrip from './ContactDashboardStrip';
import RecentlyDeletedProjects from './RecentlyDeletedProjects';
import ContactCardGrid from './ContactCardGrid';
import { getClientStatus, getProjectCount, formatDate, CLIENT_STATUS_I18N_KEY } from './contactHelpers';
import * as clientDeletionActions from '../../actions/clientDeletions';
import { formatCascadeCountsLine } from './projectDeletionHelpers';

const isMobile = window.innerWidth < 576;

interface Props {
    intl: any;
    actions: any;
    clientActions: any;
    clientDeletionActions: any;
    client: any;
    history: any;
    location: any;
    loading: boolean;
    buttonLoader: boolean;
    permissions: any;
}

const STORAGE_KEY = 'contactViewMode';

// Dashboard strip filter modes
type StripFilter = 'total' | 'new' | 'projects' | 'completedProjects' | 'noMeasure' | 'cities' | 'agents' | 'recentlyDeleted';

// Mobile toolbar filter
type FilterMode = 'all' | 'per_matje' | string; // string for agent names

const ContactsListPage: React.FC<Props> = (props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    // Columns are recomputed every render so column titles track both locale
    // changes AND the initial async message-load completion. Previously cached
    // via useMemo keyed on props.intl.locale, but locale is stable while
    // messages load asynchronously — so the memo held English defaults even
    // after Albanian messages arrived. Small array; no memoization needed.
    const columns: MikaColumn[] = [
        {
            key: 'id',
            title: '#',
            sortable: true,
            width: '50px',
            render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span>
        },
        {
            key: 'firstnameAndLastname',
            title: fm('contact.list.column.fullName', 'Full name'),
            sortable: true,
            render: (val: any, row: any) => `${val || ''} ${row.lastName || ''}`.trim() || '\u2014'
        },
        { key: 'phoneNumber', title: fm('contact.list.column.phone', 'Phone') },
        { key: 'email', title: fm('contact.list.column.email', 'Email'), hideOnMobile: true },
        { key: 'city', title: fm('contact.list.column.city', 'City'), sortable: true, hideOnMobile: true },
        { key: 'companyName', title: fm('contact.list.column.company', 'Company'), sortable: true, hideOnMobile: true },
        {
            key: 'projects',
            title: fm('contact.list.column.projectCount', 'Projects'),
            render: (_: any, row: any) => {
                const count = getProjectCount(row);
                return count > 0
                    ? <span className="clp-badge clp-badge-info">{count}</span>
                    : <span className="clp-badge clp-badge-muted">0</span>;
            }
        },
        {
            key: 'status',
            title: fm('contact.list.column.status', 'Status'),
            render: (_: any, row: any) => {
                const s = getClientStatus(row);
                return (
                    <span className="clp-status" style={{ color: s.color, background: s.bg }}>
                        <FormattedMessage id={CLIENT_STATUS_I18N_KEY[s.variant]} defaultMessage={s.variant} />
                    </span>
                );
            }
        },
        {
            key: 'createdAt',
            title: fm('contact.list.column.date', 'Date'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => formatDate(val)
        },
        {
            key: 'user.userName',
            title: fm('contact.list.column.agent', 'Agent'),
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        }
    ];
    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        status: { default: 'total', type: 'string' },
        search: { default: '', type: 'string' },
        filterMode: { default: 'all', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
        agentId: { default: '', type: 'string' },
        agentName: { default: '', type: 'string' },
    });

    // Derived state from URL filters
    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const stripFilter = filters.status as StripFilter;
    const filterMode = filters.filterMode as FilterMode;
    const dateRange: DateRange | null = (filters.dateFrom && filters.dateTo) ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeCity: string | null = filters.city || null;
    const activeAgent: { id: number; name: string } | null = filters.agentId ? { id: Number(filters.agentId), name: filters.agentName } : null;

    // Setters that write to URL
    const setCurrentPage = (p: number) => setFilter('page', p);
    const setPerPage = (p: number) => setFilters({ perPage: p, page: 1 });
    const setSearchTerm = (s: string) => replaceFilters({ search: s, page: 1 });
    const setDateRange = (d: DateRange | null) => setFilters({ dateFrom: d?.from || '', dateTo: d?.to || '', page: 1 });
    const setActiveCity = (c: string | null) => setFilter('city', c || '');
    const setActiveAgent = (a: { id: number; name: string } | null) => setFilters({ agentId: a ? String(a.id) : '', agentName: a ? a.name : '' });
    const setStripFilter = (s: StripFilter) => setFilter('status', s);
    const setFilterMode = (m: FilterMode) => setFilter('filterMode', m);

    const [filterOpen, setFilterOpen] = useState(false);
    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
    const cityRef = useRef<HTMLDivElement>(null);
    const agentRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);
    const [cityPopoverPos, setCityPopoverPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
    const [agentPopoverPos, setAgentPopoverPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(STORAGE_KEY) as 'table' | 'card') || 'table';
    });
    const [deleteClient, setDeleteClient] = useState<any>(null);
    // Cascade counts for the deletion confirm modal — populated by
    // getClientDeletionPreview just before the modal opens so the user can
    // see exactly what's about to disappear.
    const [deleteCascadeCounts, setDeleteCascadeCounts] = useState<any>(null);
    const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
    const [dashboardStatsData, setDashboardStatsData] = useState<any>(null);

    // Stats loader factored out so it can be re-invoked (e.g. after a restore action
    // inside the deletions view) to keep the strip card counts in sync.
    const loadStats = () => {
        props.clientActions.getClientDashboardStats().then((res: any) => {
            if (res) setDashboardStatsData(res);
        });
    };

    // Get start of current month for "new this month" filter
    const getMonthStart = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    };
    const getToday = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    };

    // Build combined search term from search + city filter
    const getEffectiveSearch = (search: string, city: string | null) => {
        if (city && search) return `${city} ${search}`;
        if (city) return city;
        return search;
    };

    // Unified fetch based on active strip filter (desktop) or filterMode (mobile)
    const fetchData = (page: number, pp: number, search: string, strip: StripFilter, mobile: FilterMode, city: string | null = null, agent: { id: number; name: string } | null = null, dates: DateRange | null = null) => {
        const effectiveSearch = getEffectiveSearch(search, city);
        const agentId = agent?.id ? String(agent.id) : '';
        const from = dates?.from || '';
        const to = dates?.to || '';

        if (isMobile) {
            if (mobile === 'per_matje') {
                props.clientActions.fetchClients(undefined, undefined, effectiveSearch);
            } else {
                props.clientActions.getAllClients(page, pp, effectiveSearch, from, agentId, to);
            }
            return;
        }

        switch (strip) {
            case 'new':
                props.clientActions.getAllClients(page, pp, effectiveSearch, getMonthStart(), agentId, getToday());
                break;
            case 'noMeasure':
                props.clientActions.fetchClients(undefined, undefined, effectiveSearch);
                break;
            case 'projects':
                props.clientActions.getAllClients(page, pp, effectiveSearch, from, agentId, to, 'active');
                break;
            case 'completedProjects':
                props.clientActions.getAllClients(page, pp, effectiveSearch, from, agentId, to, 'completed');
                break;
            case 'agents':
            case 'cities':
            case 'total':
            default:
                props.clientActions.getAllClients(page, pp, effectiveSearch, from, agentId, to);
                break;
        }
    };

    // Fetch stats on mount (desktop needs all stats, mobile needs agent list).
    // The dashboard-stats endpoint includes recentlyDeletedProjects count too, so this
    // single fetch powers every strip card including the superAdmin-only one.
    useEffect(() => {
        loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Recently-deleted is rendered by its own component below — it fetches its own data,
        // and the regular clients fetch would just waste a request (and confuse the backend
        // with an unrecognised status value).
        if (stripFilter === 'recentlyDeleted') return;
        fetchData(currentPage, perPage, searchTerm, stripFilter, filterMode, activeCity, activeAgent, dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.location.search]);

    const clients = props.client.clients || [];
    const pagination = props.client.pagination;

    // props.intl as a dep (not just props.intl.locale) — the intl context
    // reference changes when messages finish loading async, so the memo also
    // refreshes once real translations arrive for the current locale.
    const dashboardStats = useMemo(() => {
        const s = dashboardStatsData;
        const cards: Array<{ key: string; label: string; value: string | number; icon: string }> = [
            { key: 'total', label: fm('contact.dashboard.totalClients', 'Total clients'), value: s?.total ?? pagination?.total ?? 0, icon: 'users' },
            { key: 'new', label: fm('contact.dashboard.newThisMonth', 'New this month'), value: s?.newThisMonth ?? '\u2014', icon: 'userPlus' },
            { key: 'projects', label: fm('contact.dashboard.activeProjects', 'Active projects'), value: s?.activeProjects ?? '\u2014', icon: 'folder' },
            { key: 'completedProjects', label: fm('contact.dashboard.completedProjects', 'Completed'), value: s?.completedProjects ?? '\u2014', icon: 'check' },
            { key: 'noMeasure', label: fm('contact.dashboard.noMeasure', 'No measure'), value: s?.measuresOnHold ?? '\u2014', icon: 'alert' },
            { key: 'cities', label: fm('contact.dashboard.topCities', 'Top cities'), value: Object.keys(s?.cityDistribution || {}).length || '\u2014', icon: 'map' },
            { key: 'agents', label: fm('contact.dashboard.agents', 'Agents'), value: (s?.agentDistribution || []).length || '\u2014', icon: 'agent' },
        ];
        // "Recently Deleted" entry \u2014 visible to both superAdmin and fullPrivileges
        // (same audience that can perform the delete itself). Count comes from the
        // shared dashboard-stats response. Clicking sets the strip filter rather than
        // navigating (special-cased in handleStripClick).
        if (props.permissions?.superAdmin || props.permissions?.fullPrivileges) {
            cards.push({
                key: 'recentlyDeleted',
                label: fm('contact.recentlyDeleted.linkLabel', 'Recently Deleted'),
                value: s?.recentlyDeletedProjects ?? '\u2014',
                icon: 'restore',
            });
        }
        return cards;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardStatsData, pagination, props.intl, props.permissions]);

    // City distribution from stats endpoint
    const cityDistribution = useMemo(() => {
        const dist = dashboardStatsData?.cityDistribution;
        if (!dist) return [];
        return Object.entries(dist).sort((a: any, b: any) => b[1] - a[1]);
    }, [dashboardStatsData]);

    // Agent distribution from stats endpoint (array of {id, name, count})
    const agentDistribution: { id: number; name: string; count: number }[] = useMemo(() => {
        return dashboardStatsData?.agentDistribution || [];
    }, [dashboardStatsData]);

    // Search — just update URL, useEffect will trigger fetchData
    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    // Date range filter
    const handleDateChange = (range: DateRange | null) => {
        const updates: any = { dateFrom: range?.from || '', dateTo: range?.to || '', page: 1 };
        if (stripFilter === 'new') updates.status = 'total';
        setFilters(updates);
    };

    // Position a popover centered under a card by index
    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, right: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, right: 0 };
        const rect = card.getBoundingClientRect();
        const popoverWidth = 220;
        const cardCenter = rect.left + rect.width / 2;
        return {
            top: rect.bottom + 4,
            right: window.innerWidth - cardCenter - popoverWidth / 2,
        };
    };

    // Desktop strip filter click
    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setAgentPopoverOpen(false);
            if (!cityPopoverOpen) {
                setCityPopoverPos(positionPopoverUnderCard(4)); // cities is 5th card (index 4)
            }
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        if (key === 'agents') {
            setCityPopoverOpen(false);
            if (!agentPopoverOpen) {
                setAgentPopoverPos(positionPopoverUnderCard(5)); // agents is 6th card (index 5)
            }
            setAgentPopoverOpen(!agentPopoverOpen);
            return;
        }
        // Clicking a different filter clears city, agent, and mobile filter
        setCityPopoverOpen(false);
        setAgentPopoverOpen(false);
        const newFilter = key as StripFilter;
        if (newFilter === stripFilter) {
            setFilters({ status: 'total', page: 1, search: '', city: '', agentId: '', agentName: '', filterMode: 'all', dateFrom: '', dateTo: '' });
        } else {
            const updates: any = { status: newFilter, page: 1, search: '', city: '', agentId: '', agentName: '', filterMode: 'all' };
            if (newFilter === 'new') {
                updates.dateFrom = getMonthStart();
                updates.dateTo = getToday();
            } else {
                updates.dateFrom = '';
                updates.dateTo = '';
            }
            setFilters(updates);
        }
    };

    // Filter city from popover
    const handleCityFilter = (city: string) => {
        setFilters({ city: city, agentId: '', agentName: '', status: 'cities', page: 1 });
        setCityPopoverOpen(false);
    };

    // Clear city filter
    const clearCityFilter = () => {
        setFilters({ city: '', status: 'total', page: 1 });
    };

    // Filter agent from popover
    const handleAgentFilter = (agent: { id: number; name: string }) => {
        setFilters({ agentId: String(agent.id), agentName: agent.name, city: '', status: 'agents', page: 1 });
        setAgentPopoverOpen(false);
    };

    // Clear agent filter
    const clearAgentFilter = () => {
        setFilters({ agentId: '', agentName: '', filterMode: 'all', status: 'total', page: 1 });
    };

    // Close popovers on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
                setCityPopoverOpen(false);
            }
            if (agentRef.current && !agentRef.current.contains(e.target as Node)) {
                setAgentPopoverOpen(false);
            }
        };
        if (cityPopoverOpen || agentPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [cityPopoverOpen, agentPopoverOpen]);

    // View mode
    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(STORAGE_KEY, mode);
    };

    // Navigation
    const editClient = (client: ClientObject) => {
        props.history.push({ pathname: `/dashboard/contact/create/${client.id}` });
    };

    const handleRemove = (client: ClientObject) => {
        // Fetch the cascade preview BEFORE opening the modal so we can show
        // the user "this deletes N measures, M offers, …" upfront — same
        // pattern as the project-delete flow in CreateContactForm /
        // CreateProjectModal. Empty counts (no cascade) is still a valid
        // result; the modal renders a simpler "are you sure?" message in
        // that case.
        if (deletePreviewLoading) return;
        setDeletePreviewLoading(true);
        props.clientDeletionActions.getClientDeletionPreview(client.id).then((res: any) => {
            const counts = res?.data?.data?.affected_counts || null;
            setDeleteCascadeCounts(counts);
            setDeleteClient(client);
        }).finally(() => setDeletePreviewLoading(false));
    };

    const confirmDelete = (note?: string) => {
        if (!deleteClient) return;
        props.clientActions.deleteClient(deleteClient.id, note).then(() => {
            // Refresh BOTH the visible list and the strip-card stats — deleting a client
            // changes Total clients (−1), Recently Deleted (+1), and potentially Active
            // projects / No measure / Top cities / Agents depending on what the client
            // had attached. Mirrors the pattern in RecentlyDeletedProjects.tsx where the
            // restore path calls onMutate → loadStats.
            //
            // Sibling delete call sites (CreateContactForm, CreateProjectModal) live on
            // a different route, so they don't need this — when the user navigates back
            // to /dashboard/contact, ContactsListPage remounts and loadStats() fires from
            // the mount effect.
            fetchData(currentPage, perPage, searchTerm, stripFilter, filterMode, activeCity, activeAgent, dateRange);
            loadStats();
            setDeleteClient(null);
            setDeleteCascadeCounts(null);
        });
    };

    // CSV export
    const exportColumns: ExportColumn[] = [
        { key: 'firstnameAndLastname', title: fm('contact.list.column.fullName', 'Full name') },
        { key: 'lastName', title: fm('contact.list.column.lastName', 'Last name') },
        { key: 'phoneNumber', title: fm('contact.list.column.phone', 'Phone') },
        { key: 'email', title: fm('contact.list.column.email', 'Email') },
        { key: 'city', title: fm('contact.list.column.city', 'City') },
        { key: 'companyName', title: fm('contact.list.column.company', 'Company') },
        { key: 'createdAt', title: fm('contact.list.column.date', 'Date') },
    ];

    const fetchAllForExport = async (): Promise<any[]> => {
        const effectiveSearch = getEffectiveSearch(searchTerm, activeCity);
        const agentId = activeAgent?.id ? String(activeAgent.id) : '';
        const from = dateRange?.from || '';
        const to = dateRange?.to || '';
        let url = `clients?per_page=0`;
        if (effectiveSearch) url += `&search=${encodeURIComponent(effectiveSearch)}`;
        if (from) url += `&created_from=${encodeURIComponent(from)}`;
        if (to) url += `&created_to=${encodeURIComponent(to)}`;
        if (agentId) url += `&user_id=${agentId}`;
        if (stripFilter === 'projects') url += '&project_filter=active';
        if (stripFilter === 'completedProjects') url += '&project_filter=completed';
        const res = await axiosCaller('GET', url);
        return res?.data?.data || [];
    };

    const localizedContactsTitle = fm('contact.list.title', 'Contacts');
    const exportTitle = activeCity ? `${localizedContactsTitle} - ${activeCity}`
        : activeAgent ? `${localizedContactsTitle} - ${activeAgent.name}`
        : localizedContactsTitle;

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerPageChange = (value: number) => setPerPage(value);

    // Table actions. Delete is gated to superAdmin/fullPrivileges to match the backend
    // (ClientService::deleteClient enforces the same; this just stops a user without
    // the perm from clicking a button that would silently no-op for them).
    const canDeleteClients = props.permissions?.superAdmin || props.permissions?.fullPrivileges;
    const tableActions = [
        {
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
            ),
            tooltip: fm('contact.list.edit', 'Edit'),
            onClick: editClient
        },
        ...(canDeleteClients ? [{
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
            ),
            tooltip: fm('contact.list.delete', 'Delete'),
            onClick: handleRemove
        }] : []),
    ];

    const effectiveView = isMobile ? 'table' : viewMode;

    const deleteClientName = deleteClient
        ? `${deleteClient.firstnameAndLastname || ''} ${deleteClient.lastName || ''}`.trim()
        : '';

    return (
        <div className="clp-page">
            {/* Desktop: full page header */}
            {!isMobile && (
                <MikaPageHeader
                    title={localizedContactsTitle}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                    actions={
                        <button
                            className="clp-create-btn"
                            onClick={() => props.history.push('/dashboard/contact/create')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <FormattedMessage id="contact.list.createNew" defaultMessage="Create new" />
                        </button>
                    }
                />
            )}

            {/* Toolbar: compact on mobile, search bar on desktop */}
            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className="clp-search-input"
                        placeholder={filterMode === 'per_matje'
                            ? fm('contact.list.searchForMeasurePlaceholder', 'Search for measurement...')
                            : fm('contact.list.searchPlaceholder', 'Search contacts...')}
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearchChange('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    {/* Mobile only: combined filter dropdown with agents */}
                    {isMobile && (
                        <div className="clp-filter-dropdown" tabIndex={0} onBlur={() => setTimeout(() => setFilterOpen(false), 150)}>
                            <button
                                className={`clp-filter-trigger ${filterMode !== 'all' ? 'clp-filter-active' : ''}`}
                                onClick={() => setFilterOpen(!filterOpen)}
                                type="button"
                            >
                                <span>{filterMode === 'all'
                                    ? fm('contact.list.filter.all', 'All')
                                    : filterMode === 'per_matje'
                                        ? fm('contact.list.filter.forMeasure', 'For measure')
                                        : activeAgent?.name || filterMode}</span>
                                <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ transform: filterOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                                    <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            {filterOpen && (
                                <div className="clp-filter-menu">
                                    <button
                                        className={`clp-filter-option ${filterMode === 'all' ? 'clp-filter-option-active' : ''}`}
                                        onClick={() => { setFilters({ filterMode: 'all', agentId: '', agentName: '', city: '', status: 'total', page: 1, search: '' }); setFilterOpen(false); }}
                                        type="button"
                                    ><FormattedMessage id="contact.list.filter.all" defaultMessage="All" /></button>
                                    <button
                                        className={`clp-filter-option ${filterMode === 'per_matje' ? 'clp-filter-option-active' : ''}`}
                                        onClick={() => { setFilters({ filterMode: 'per_matje', agentId: '', agentName: '', city: '', status: 'total', page: 1, search: '' }); setFilterOpen(false); }}
                                        type="button"
                                    ><FormattedMessage id="contact.list.filter.forMeasure" defaultMessage="For measure" /></button>
                                    {agentDistribution.length > 0 && (
                                        <>
                                            <div className="clp-filter-separator" />
                                            {agentDistribution.map((agent) => (
                                                <button
                                                    key={agent.id}
                                                    className={`clp-filter-option ${activeAgent?.id === agent.id ? 'clp-filter-option-active' : ''}`}
                                                    onClick={() => { setFilters({ filterMode: 'agent', agentId: String(agent.id), agentName: agent.name, city: '', status: 'agents', page: 1, search: '' }); setFilterOpen(false); }}
                                                    type="button"
                                                >
                                                    <span>{agent.name}</span>
                                                    <span className="clp-filter-option-count">{agent.count}</span>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <MikaDateFilter value={dateRange} onChange={handleDateChange} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('contact.list.exportFilename', 'contacts')} title={exportTitle} companyProfile={(props as any).companyProfile} />
                    {isMobile && (
                        <button className="clp-create-btn" onClick={() => props.history.push('/dashboard/contact/create')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            <div ref={stripRef}>
                        <ContactDashboardStrip
                            stats={dashboardStats}
                            activeFilter={stripFilter}
                            onFilterClick={handleStripClick}
                        />
                    </div>
                    {cityPopoverOpen && (
                        <div className="clp-city-popover" ref={cityRef} style={{ top: cityPopoverPos.top, right: cityPopoverPos.right }}>
                            <div className="clp-city-header">
                                <FormattedMessage id="contact.dashboard.topCities" defaultMessage="Top cities" />
                            </div>
                            {cityDistribution.length === 0 ? (
                                <div className="clp-city-empty">
                                    <FormattedMessage id="contact.list.noData" defaultMessage="No data available" />
                                </div>
                            ) : (
                                cityDistribution.map(([city, count]) => (
                                    <button
                                        key={city}
                                        className={`clp-city-row ${activeCity === city ? 'clp-city-row-active' : ''}`}
                                        onClick={() => handleCityFilter(city)}
                                        type="button"
                                    >
                                        <span className="clp-city-name">{city}</span>
                                        <span className="clp-city-count">{count}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                    {agentPopoverOpen && (
                        <div className="clp-city-popover" ref={agentRef} style={{ top: agentPopoverPos.top, right: agentPopoverPos.right }}>
                            <div className="clp-city-header">
                                <FormattedMessage id="contact.dashboard.agents" defaultMessage="Agents" />
                            </div>
                            {agentDistribution.length === 0 ? (
                                <div className="clp-city-empty">
                                    <FormattedMessage id="contact.list.noData" defaultMessage="No data available" />
                                </div>
                            ) : (
                                agentDistribution.map((agent) => (
                                    <button
                                        key={agent.id}
                                        className={`clp-city-row ${activeAgent?.id === agent.id ? 'clp-city-row-active' : ''}`}
                                        onClick={() => handleAgentFilter(agent)}
                                        type="button"
                                    >
                                        <span className="clp-city-name">{agent.name}</span>
                                        <span className="clp-city-count">{agent.count}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

            {(activeCity || activeAgent) && (
                <div className="clp-active-filter-bar">
                    {activeCity && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                            </svg>
                            <span>{activeCity}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="contact.list.pagination.clients" defaultMessage="clients" /></span>
                            <button className="clp-active-filter-clear" onClick={clearCityFilter} type="button">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {activeAgent && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                            <span>{activeAgent.name}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="contact.list.pagination.clients" defaultMessage="clients" /></span>
                            <button className="clp-active-filter-clear" onClick={clearAgentFilter} type="button">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {stripFilter === 'recentlyDeleted' ? (
                <RecentlyDeletedProjects onMutate={loadStats} />
            ) : (
                <>
                    {effectiveView === 'table' ? (
                        <MikaTable
                            columns={columns}
                            data={clients}
                            actions={tableActions}
                            loading={props.loading}
                            onRowClick={editClient}
                        />
                    ) : (
                        <ContactCardGrid
                            data={clients}
                            onEdit={editClient}
                            onDelete={canDeleteClients ? handleRemove : undefined}
                        />
                    )}

                    <PaginationComponent
                        currentPage={currentPage}
                        perPage={perPage}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                        label={fm('contact.list.pagination.clients', 'clients')}
                    />
                </>
            )}

            <MikaConfirmModal
                isOpen={!!deleteClient}
                onClose={() => { setDeleteClient(null); setDeleteCascadeCounts(null); }}
                onConfirm={confirmDelete}
                title={fm('contact.list.deleteClient', 'Delete client')}
                message={(() => {
                    // Cascade-aware confirm message. When the preview returned a
                    // non-empty manifest, show "this deletes N measures, M offers
                    // …" inline; otherwise fall back to the generic confirm.
                    const cascadeNode = formatCascadeCountsLine(deleteCascadeCounts);
                    return cascadeNode ? (
                        <FormattedMessage
                            id="contact.list.confirmDeleteMessage.withCascade"
                            defaultMessage="Delete the client {name}? This will also delete {items}. Recoverable from Recently Deleted."
                            values={{ name: deleteClientName, items: cascadeNode }}
                        />
                    ) : (
                        <FormattedMessage
                            id="contact.list.confirmDeleteMessage"
                            defaultMessage="Are you sure you want to delete the client {name}?"
                            values={{ name: deleteClientName }}
                        />
                    );
                })()}
                noteInput={{}}
                confirmLabel={fm('contact.list.delete', 'Delete')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        client: state.client,
        user: state.user,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        clientActions: bindActionCreators(clientActions as any, dispatch),
        clientDeletionActions: bindActionCreators(clientDeletionActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ContactsListPage as any)));
